<template>
	<article class="flex items-center justify-between" :class="borderClasses">
		<div class="space-y-2">
			<h2 class="text-xl font-semibold">
				{{ title }}
			</h2>
			<div class="flex flex-wrap gap-1">
				<AppPill
					v-for="pill in pills"
					:key="pills.indexOf(pill)"
					:icon="pill.icon"
					:palette="pill.palette"
				>
					{{ pill.text }}
				</AppPill>
			</div>
		</div>
		<slot />
	</article>
</template>

<script lang="ts" setup>
import type { ReadLoanOffer } from "@/generatedApi";
import { computed } from "vue";

interface Props {
	offer: ReadLoanOffer;
	title?: string;
	border?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	border: true,
});

const borderClasses = computed(() => ({
	border: props.border,
}));

// get days until specified date
function getDaysUntil(date: string) {
	const delta = new Date(date).getTime() - new Date(Date.now()).getTime();
	return Math.round(delta / (1000 * 3600 * 24));
}

// get a pill for representing a deadline
// varies icon and palette based on wheter the date is in future or not
function getDeadlinePill(date: string, text: string) {
	const days = getDaysUntil(date);
	return {
		icon: days <= 0 ? "cancel" : "calendar_clock",
		palette: days <= 0 ? "red" : "amber",
		text,
	};
}

const pills = computed(() => {
	const pills = [];
	if (props.offer.passingDate) {
		pills.push(
			getDeadlinePill(
				props.offer.passingDate,
				`Passeerdatum: ${props.offer.passingDate}`,
			),
		);
	}

	if (props.offer.acceptanceDate) {
		pills.push(
			getDeadlinePill(
				props.offer.acceptanceDate,
				`Accepteren voor: ${props.offer.acceptanceDate}`,
			),
		);
	}

	if (props.offer.mustReturnSignedCopy) {
		pills.push({
			icon: "signature",
			palette: "amber",
			text: "Document getekend retour",
		});
	}

	if (props.offer.extensionPossible) {
		pills.push({
			icon: "more_time",
			palette: "zinc",
			text: "Verlenging mogelijk",
		});
	}

	return pills;
});
</script>
